import { z } from "zod";
import { ROLE_NAMES, ROLES, SYSTEM_ROLE_NAMES } from "./const.js";
import { isCoreInstance, type InstanceConfig } from "./instance.js";

// =====================================================================
// Rollen, Scopes und individuelle Rollen (Migrationen 0053, 0059)
// =====================================================================
// app_roles ist global eindeutig, die Herkunft ist jedoch strikt getrennt:
// bestehende Firmenrollen bleiben `combsol`; neue Core-Rollen sind `core`.
// Die Serverinstanz bestimmt den Scope. Kein Client kann ihn mitgeben.
// =====================================================================

export const ROLE_SCOPES = {
  COMBSOL: "combsol",
  CORE: "core",
} as const;
export type RoleScope = (typeof ROLE_SCOPES)[keyof typeof ROLE_SCOPES];

export function roleScopeForInstance(config: Pick<InstanceConfig, "productName">): RoleScope {
  return isCoreInstance(config) ? ROLE_SCOPES.CORE : ROLE_SCOPES.COMBSOL;
}

/** app_roles.key ist varchar(16). */
export const ROLE_KEY_MIN = 2;
export const ROLE_KEY_MAX = 16;
/** app_roles.label ist varchar(64). */
export const ROLE_LABEL_MIN = 2;
export const ROLE_LABEL_MAX = 64;

/**
 * Buchstabe am Anfang, danach Buchstaben/Ziffern, Bindestrich nur als
 * Trenner (kein Bindestrich am Ende, keine doppelten). Nur ASCII, damit der
 * Schluessel in Cookies, JWT-Claims und Satelliten-Code unproblematisch ist.
 */
export const ROLE_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9]*(?:-[A-Za-z0-9]+)*$/;

/**
 * Reservierte Schluessel, Vergleich ohne Beachtung der Gross-/Kleinschreibung:
 * alle Systemrollen plus Altrollen aus frueheren Rollenmodellen (OPM, OM, PL),
 * die in Satelliten oder Altdaten noch als Rollenname vorkommen koennen, sowie
 * "nexus": Rollenname der Dienstidentitaet (shared/nexus-auth.ts). Kein
 * Nutzer darf eine Rolle tragen, die Satelliten als Dienst lesen wuerden.
 */
export const RESERVED_ROLE_KEYS: readonly string[] = [...SYSTEM_ROLE_NAMES, "OPM", "OM", "PL", "nexus"];

export function isReservedRoleKey(key: string): boolean {
  const k = key.trim().toLowerCase();
  return RESERVED_ROLE_KEYS.some(r => r.toLowerCase() === k);
}

/** Systemrolle = als System markiert ODER kanonischer/reservierter Schluessel. */
export function isSystemRole(role: { key: string; isSystem?: boolean | null }): boolean {
  return role.isSystem === true || isReservedRoleKey(role.key);
}

/** Rollen mit fest vorgegebenen App-Freigaben (Mitarbeiter-PA: BackOffice). */
export const FIXED_ROLE_APP_GRANTS: Readonly<Record<string, readonly string[]>> = {
  [ROLES.MITARBEITER_PA]: ["back-office"],
};

/** Feste App-Freigaben der Rolle oder null (eigene Eigenschaft, kein Prototyp). */
export function fixedAppsForRole(roleKey: string): readonly string[] | null {
  return Object.prototype.hasOwnProperty.call(FIXED_ROLE_APP_GRANTS, roleKey)
    ? FIXED_ROLE_APP_GRANTS[roleKey]
    : null;
}

export function isRolePermissionLocked(roleKey: string): boolean {
  return fixedAppsForRole(roleKey) !== null;
}

/** Drizzle liest varchar als string; die Datenbank-Constraint begrenzt ihn auf zwei Werte. */
function normalizedRoleScope(scope: string | RoleScope | null | undefined): RoleScope {
  return scope === ROLE_SCOPES.CORE ? ROLE_SCOPES.CORE : ROLE_SCOPES.COMBSOL;
}

/**
 * Darf diese Rolle eine Rollenfreigabe für die App bekommen? Rollen sind im
 * Core ausschließlich historische Bestandsdaten. Alle dortigen Appzugriffe
 * werden nach Migration 0060 als explizite Person×App-Freigabe geführt.
 * Firmenrollen bleiben unverändert.
 */
export function canRoleReceiveAppGrant(role: { key: string; roleScope: string | RoleScope }, appKey: string): boolean {
  if (isRolePermissionLocked(role.key) || appKey === "life") return false;
  return normalizedRoleScope(role.roleScope) !== ROLE_SCOPES.CORE;
}

/**
 * Zählt eine gespeicherte Rollenfreigabe für die App? Eine fehlerhafte oder
 * historische Core-Matrixzeile darf keine Core-Person öffnen. Firmenmatrix
 * bleibt vollständig kompatibel; Life bleibt dort außer für Test individuell.
 */
export function roleGrantsApp(roleKey: string, appKey: string, roleScope: string | RoleScope = ROLE_SCOPES.COMBSOL): boolean {
  const fixed = fixedAppsForRole(roleKey);
  if (fixed && !fixed.includes(appKey)) return false;
  if (appKey === "life") return roleKey === ROLES.TEST;
  return normalizedRoleScope(roleScope) !== ROLE_SCOPES.CORE;
}

// Steuerzeichen (inkl. Zeilenumbruch) sind im Anzeigenamen nicht erlaubt.
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

export const roleLabelSchema = z.string()
  .trim()
  .min(ROLE_LABEL_MIN, `Anzeigename: mindestens ${ROLE_LABEL_MIN} Zeichen`)
  .max(ROLE_LABEL_MAX, `Anzeigename: höchstens ${ROLE_LABEL_MAX} Zeichen`)
  .refine(l => !CONTROL_CHARS.test(l), "Anzeigename enthält Steuerzeichen");

export const createRoleInputSchema = z.object({
  key: z.string()
    .trim()
    .min(ROLE_KEY_MIN, `Schlüssel: mindestens ${ROLE_KEY_MIN} Zeichen`)
    .max(ROLE_KEY_MAX, `Schlüssel: höchstens ${ROLE_KEY_MAX} Zeichen`)
    .regex(ROLE_KEY_PATTERN, "Schlüssel: nur Buchstaben, Ziffern und einzelne Bindestriche, Beginn mit Buchstabe")
    .refine(k => !isReservedRoleKey(k), "Schlüssel ist für eine Systemrolle reserviert"),
  label: roleLabelSchema,
}).strict();

export type CreateRoleInput = z.infer<typeof createRoleInputSchema>;

/** Stable key bleibt unveraendert; nur der individuelle Anzeigename ist editierbar. */
export const updateRoleLabelInputSchema = z.object({
  roleId: z.number().int().positive(),
  label: roleLabelSchema,
}).strict();
